import * as React from "react";
import { getAgreementIdentifiersFromDocument } from "../agreement";
import { postSessionTime } from "../api/usage";

/** Interval between heartbeat POSTs while the task pane document is visible. */
const HEARTBEAT_INTERVAL_MS = 1000;

/**
 * Tracks active add-in time (document visible) and POSTs to `/contracts/add_in/session_time`:
 * - Loads `contract_id` / `contract_version_id` from document custom properties.
 * - `session_start`: no `session_id`; backend returns `session_id`.
 * - `heartbeat`: includes latest `session_id` + `elapsed_ms`; backend may return an updated `session_id`.
 */
export function useUsageTracker(): void {
  const sessionIdRef = React.useRef<string | null>(null);
  const contractIdRef = React.useRef<string | null>(null);
  const contractVersionIdRef = React.useRef<string | null>(null);
  const segmentStartRef = React.useRef<number>(Date.now());
  const intervalRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    let cancelled = false;

    const clearHeartbeat = (): void => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const applySessionIdFromResponse = (id: string | undefined): void => {
      if (id) {
        sessionIdRef.current = id;
      }
    };

    const tickHeartbeat = (): void => {
      const sid = sessionIdRef.current;
      const cid = contractIdRef.current;
      const cvid = contractVersionIdRef.current;
      if (!sid || !cid || !cvid) {
        return;
      }
      const now = Date.now();
      const elapsed = now - segmentStartRef.current;
      segmentStartRef.current = now;
      void postSessionTime({
        type: "heartbeat",
        session_id: sid,
        elapsed_ms: elapsed,
        contract_id: cid,
        contract_version_id: cvid,
      })
        .then(applySessionIdFromResponse)
        .catch(() => {
          /* fire-and-forget */
        });
    };

    const startHeartbeatLoop = (): void => {
      clearHeartbeat();
      if (!sessionIdRef.current) {
        return;
      }
      intervalRef.current = window.setInterval(tickHeartbeat, HEARTBEAT_INTERVAL_MS);
    };

    const ensureContractIds = async (): Promise<boolean> => {
      if (contractIdRef.current && contractVersionIdRef.current) {
        return true;
      }
      try {
        const ids = await getAgreementIdentifiersFromDocument();
        contractIdRef.current = ids.agreementId;
        contractVersionIdRef.current = ids.agreementVersionId;
        return true;
      } catch {
        return false;
      }
    };

    const runVisible = async (): Promise<void> => {
      if (document.visibilityState !== "visible") {
        return;
      }

      const hasIds = await ensureContractIds();
      if (!hasIds || cancelled) {
        return;
      }

      const cid = contractIdRef.current!;
      const cvid = contractVersionIdRef.current!;

      if (!sessionIdRef.current) {
        try {
          const id = await postSessionTime({
            type: "session_start",
            contract_id: cid,
            contract_version_id: cvid,
          });
          if (cancelled) {
            return;
          }
          applySessionIdFromResponse(id);
          segmentStartRef.current = Date.now();
        } catch {
          return;
        }
      } else {
        segmentStartRef.current = Date.now();
      }

      startHeartbeatLoop();
    };

    const onHidden = (): void => {
      clearHeartbeat();
    };

    const onVisibilityChange = (): void => {
      if (document.visibilityState === "hidden") {
        onHidden();
      } else {
        void runVisible();
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    void runVisible();

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearHeartbeat();
    };
  }, []);
}
